import type { BaseSelectProps, FormControlProps, SelectChangeEvent } from "@mui/material";
import {
  Alert,
  FormControl,
  FormHelperText,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { sortBy } from "lodash-es";
import type { FC } from "react";
import { useCallback } from "react";
import { config } from "../../../config";
import { useSupabaseLoader } from "../../../server/supabase/hooks";
import { decodeWords } from "../../../utils/mime";
import type { UUID } from "../../../utils/uuid";
import { SupabaseCustomEmailService } from "../../channels/email/server/supabase-custom-email-service";
import { RestrictedChip } from "../../moment/restricted-chip";

type EmailAddressSelectProps = Omit<BaseSelectProps<string>, "value" | "onChange"> & {
  value: string;
  onChange: (value: string) => void;
  restrictedEmailFrom: UUID[];
  labelProps?: FormControlProps;
  helperText?: string;
  hasError?: boolean;
};

/** Select showing verified sending addresses. */
export const EmailAddressSelect: FC<EmailAddressSelectProps> = ({
  value,
  onChange,
  restrictedEmailFrom,
  labelProps,
  helperText,
  hasError,
  ...props
}) => {
  const { data, loading, error } = useSupabaseLoader(
    async ({ supabase, abortController }) => {
      const { data: innerData, error: err } = await new SupabaseCustomEmailService(
        supabase,
      ).getAllVerified(value);

      if (err) {
        if (!abortController?.signal?.aborted)
          console.error("Error loading custom email addresses", err);
        return [];
      }

      return sortBy(
        innerData.map((x) => ({
          id: x.id,
          address: x.address,
          display: x.from_name ? `${decodeWords(x.from_name)} <${x.address}>` : x.address,
          restricted: restrictedEmailFrom.includes(x.id),
        })),
        "display",
      );
    },
    [restrictedEmailFrom, value],
  );

  const handleChange = useCallback(
    (event: SelectChangeEvent<string>): void => {
      onChange(event.target.value);
    },
    [onChange],
  );

  const selected = data?.find(({ address }) => address === value);

  if (loading || error) return null;

  return (
    <Stack spacing={1} direction="column">
      <FormControl
        variant="standard"
        sx={{ minWidth: 120 }}
        {...labelProps}
        error={hasError}
        fullWidth
      >
        <InputLabel id="email-address-select-label" variant="outlined">
          From
        </InputLabel>
        <Select
          labelId="email-address-select-label"
          value={value}
          label="From"
          onChange={handleChange}
          variant="outlined"
          {...props}
        >
          <MenuItem value={config.defaultEmailFrom}>{config.defaultEmailFrom}</MenuItem>
          {data.map(({ display, address, restricted }) => (
            <MenuItem value={address} key={address}>
              <Stack direction="row" spacing={1}>
                <Typography>{display}</Typography>
                {restricted && <RestrictedChip />}
              </Stack>
            </MenuItem>
          ))}
        </Select>
        {helperText && <FormHelperText error={hasError}>{helperText}</FormHelperText>}
      </FormControl>
      {data.length === 0 && (
        <Alert severity="warning">
          <Typography variant="body2">
            {`You do not have any custom email addresses configured. By default the email will be sent from '${config.defaultEmailFrom}'.`}
          </Typography>
          <Typography variant="body2">
            <Link href="/channels/email/custom-address">Configure an email address here</Link>
          </Typography>
        </Alert>
      )}
      {selected?.restricted && (
        <Alert severity="warning">
          You don’t have permission to publish using this sender. You can save the Communication as
          a draft and have someone else publish, or choose a different sender.
        </Alert>
      )}
    </Stack>
  );
};
